import { useCurrentFrame, useVideoConfig, interpolate, Easing, AbsoluteFill } from "remotion";
import { loadFont } from "@remotion/google-fonts/Inter";
import { loadFont as loadDev } from "@remotion/google-fonts/NotoSansDevanagari";
import type { StatCardData, StatRow } from "./types";

const { fontFamily } = loadFont("normal", { weights: ["400", "600", "700", "800"], subsets: ["latin"] });
const { fontFamily: devFont } = loadDev("normal", { weights: ["400", "700"], subsets: ["devanagari"] });
const pickFont = (s?: string) => (s && /[ऀ-ॿ]/.test(s) ? devFont : fontFamily);

const GOLD = "#D4A853";
const BG = "rgba(0,0,0,0.82)";
const BORDER_GOLD = "rgba(212,168,83,0.35)";

export const StatCard: React.FC<{ data: StatCardData; durationInFrames: number; top?: number; bottom?: number }> = ({ data, durationInFrames, top, bottom = 100 }) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  const fadeIn = interpolate(frame, [0, Math.round(0.35 * fps)], [0, 1], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
  });
  const fadeOut = interpolate(frame, [durationInFrames - Math.round(0.5 * fps), durationInFrames], [1, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });
  const slideX = interpolate(frame, [0, Math.round(0.4 * fps)], [60, 0], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });

  // ── Hero mode (FF BigStat-style centered count-up, temple palette) ──────────
  // Transparent, text-shadow only (no card bg), centered → never collides with
  // corner tags. Falls back to the table layout below when value_* is absent.
  const isHero = data.value_num !== undefined || data.value_str !== undefined;
  if (isHero) {
    const TS = "0 3px 14px rgba(0,0,0,0.92), 0 1px 5px rgba(0,0,0,0.95), 0 0 2px rgba(0,0,0,0.9)";
    const countProg = interpolate(frame, [0, Math.round(0.5 * fps)], [0, 1], {
      extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.bezier(0.16, 1, 0.3, 1),
    });
    const scaleIn = interpolate(frame, [0, Math.round(0.32 * fps)], [0.82, 1], {
      extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.bezier(0.16, 1, 0.3, 1),
    });
    const lineW = interpolate(frame, [Math.round(0.2 * fps), Math.round(0.7 * fps)], [0, 180], {
      extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.bezier(0.16, 1, 0.3, 1),
    });
    const dec = data.decimals ?? 0;
    const display = data.value_str !== undefined
      ? data.value_str
      : ((data.value_num as number) * countProg).toLocaleString("en-IN", { minimumFractionDigits: dec, maximumFractionDigits: dec });

    return (
      <div style={{ position: "absolute", top: 0, left: 0, width, height, opacity: fadeIn * fadeOut, pointerEvents: "none", display: "flex", alignItems: "center", justifyContent: "center", fontFamily }}>
        <div style={{ textAlign: "center", transform: `scale(${scaleIn})` }}>
          {data.headline && (
            <div style={{ color: GOLD, fontSize: 22, fontWeight: 700, letterSpacing: "3px", textTransform: "uppercase", textShadow: TS, marginBottom: 10, fontFamily: pickFont(data.headline) }}>
              {data.headline}
            </div>
          )}
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 6 }}>
            {data.prefix && <span style={{ color: "#fff", fontSize: 64, fontWeight: 800, textShadow: TS }}>{data.prefix}</span>}
            <span style={{ color: "#fff", fontSize: 120, fontWeight: 800, letterSpacing: "-2px", lineHeight: 1, textShadow: TS }}>{display}</span>
            {data.suffix && <span style={{ color: GOLD, fontSize: 54, fontWeight: 800, textShadow: TS }}>{data.suffix}</span>}
          </div>
          <div style={{ width: lineW, height: 3, background: GOLD, margin: "14px auto 0", boxShadow: `0 0 12px ${GOLD}` }} />
          {data.sub && (
            <div style={{ color: "rgba(255,255,255,0.82)", fontSize: 18, fontWeight: 600, letterSpacing: "1px", textShadow: TS, marginTop: 12, fontFamily: pickFont(data.sub) }}>
              {data.sub}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Table mode (existing) ───────────────────────────────────────────────────
  return (
    <div style={{ position: "absolute", top: 0, left: 0, width, height, opacity: fadeIn * fadeOut, pointerEvents: "none" }}>
      <div
        style={{
          position: "absolute", ...(top !== undefined ? { top } : { bottom }), right: 40,
          transform: `translateX(${slideX}px)`,
          background: BG,
          border: `1px solid ${BORDER_GOLD}`,
          borderRadius: 8,
          padding: "10px 18px",
          backdropFilter: "blur(12px)",
          boxShadow: "0 4px 24px rgba(0,0,0,0.6)",
          minWidth: 220,
        }}
      >
        {(data.stats || []).map((stat: StatRow, i: number) => (
          <div key={i}>
            {i > 0 && (
              <div style={{ height: 1, background: "rgba(212,168,83,0.2)", margin: "10px 0" }} />
            )}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 20 }}>
              <div style={{
                color: "rgba(255,255,255,0.6)", fontSize: 11, fontWeight: 600, fontFamily: pickFont(stat.label),
                textTransform: "uppercase", letterSpacing: "0.8px", flexShrink: 0,
              }}>
                {stat.label}
              </div>
              <div style={{ textAlign: "right" }}>
                <span style={{ color: GOLD, fontSize: 14, fontWeight: 700, fontFamily }}>
                  {stat.value}
                </span>
                {stat.value_secondary && (
                  <span style={{ color: "rgba(212,168,83,0.6)", fontSize: 11, fontWeight: 600, fontFamily, marginLeft: 6 }}>
                    {stat.value_secondary}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
